import { ArrowSquareOut } from "@phosphor-icons/react"
import { useLocation } from "@tanstack/react-router"
import { type CustomTooltipProps } from "ag-grid-react"

import { VARIANT_RULE_FIELD_BY_KEY, variantRuleFilterFields } from "~/components/rules/ruleFields"
import type { RulesTooltipValue } from "~/components/planning/ProductTable/columns/rules"
import { useFilterFields } from "~/hooks/useFilterFields"
import { useUserLookup } from "~/hooks/useUsers"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { FilterSummaryPill } from "~/components/planning/ViewDetailsHoverCard"
import { RuleDot, useRuleById } from "~/components/rules/ruleColors"
import { MATCH_STATUS_FIELD } from "~/components/demand-2/shared/matchStatusFilter"
import { useOpenInAssortment } from "~/components/demand-2/shared/useOpenInAssortment"
import { useOpenInPerformance } from "~/components/demand-2/shared/useOpenInPerformance"
import { PERFORMANCE_RULE_FIELDS } from "~/components/rules/usePerformanceRuleStamp"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { Button } from "~/components/design-system/components"
import type { CollectionKind } from "~/utils/collectionKind"
import { formatCount } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import HeaderTooltip from "./HeaderTooltip"
import TooltipMessageCard from "./TooltipMessageCard"

function formatConditionValue(field: string, value: unknown): string | null {
  if (value == null || value === "") return null
  const format = VARIANT_RULE_FIELD_BY_KEY.get(field)?.formatValue
  return format && typeof value === "number" ? format(value) : String(value)
}

// Scrolls rather than running off screen when a variant matches many rules; the grid keeps an
// interactive tooltip open under the pointer, so the list can be scrolled.
const CARD_CLASS = "max-w-90 max-h-[min(400px,70vh)] overflow-y-auto flex flex-col px-3"

type DemandLinkTarget = "assortment" | "performance"

/** Where a condition's data lives in Demand, for a link to see it there. */
const linkTargetOf = (field: string): DemandLinkTarget | null => {
  if (field === MATCH_STATUS_FIELD) return "assortment"
  return PERFORMANCE_RULE_FIELDS.includes(field) ? "performance" : null
}

/** A link to the variant on another Demand tab; only inside a collection, and not to the tab in view. */
function DemandLink({ target, variantId }: { target: DemandLinkTarget; variantId: string }) {
  const { isPortfolio, scopeId, kind } = useDemand2Scope()
  const onPerformance = useLocation({ select: (l) => l.pathname.endsWith("/performance") })
  if (isPortfolio || !kind || (target === "performance" && onPerformance)) return null
  return target === "assortment" ? (
    <AssortmentLink collectionId={scopeId} kind={kind} variantId={variantId} />
  ) : (
    <PerformanceLink collectionId={scopeId} kind={kind} variantId={variantId} />
  )
}

type LinkProps = { collectionId: number; kind: CollectionKind; variantId: string }

const DemandLinkButton = ({ onClick, children }: { onClick: () => void; children: string }) => (
  <Button size="xs" variant="neutral" className="gap-1" onClick={onClick}>
    {children}
    <ArrowSquareOut />
  </Button>
)

function AssortmentLink({ collectionId, kind, variantId }: LinkProps) {
  const open = useOpenInAssortment(collectionId, kind)
  return <DemandLinkButton onClick={() => open(variantId)}>Open in Assortment</DemandLinkButton>
}

function PerformanceLink({ collectionId, kind, variantId }: LinkProps) {
  const open = useOpenInPerformance(collectionId, kind)
  return <DemandLinkButton onClick={() => open(variantId)}>Open in Performance</DemandLinkButton>
}

export default function RulesTooltip(
  props: CustomTooltipProps<IVariantSummary, RulesTooltipValue>,
) {
  const { allFilterFields } = useFilterFields("", variantRuleFilterFields)
  const { getUserName } = useUserLookup()
  const ruleById = useRuleById()

  if (props.location === "header") return <HeaderTooltip {...props} />
  const value = props.value
  if (!value) return null

  if (value.kind === "group") {
    return (
      <TooltipMessageCard className={cn(CARD_CLASS, "gap-1.5 py-2.5")}>
        <span className="text-fg-subtle">
          Rules matched by {formatCount(value.variantCount, "variant")}
        </span>
        {value.counts.flatMap(({ ruleId, count }) => {
          const rule = ruleById.get(ruleId)
          return rule ? (
            <div key={ruleId} className="flex items-center gap-2">
              <RuleDot color={rule.color} />
              <span className="label-sm min-w-0 flex-1 truncate">{rule.name}</span>
              <span className="text-fg-subtle tabular-nums">{count}</span>
            </div>
          ) : (
            []
          )
        })}
      </TooltipMessageCard>
    )
  }

  return (
    <TooltipMessageCard className={cn(CARD_CLASS, "divide-y divide-border-base py-0.5")}>
      {value.matches.flatMap((match) => {
        const rule = ruleById.get(match.ruleId)
        if (!rule) return []
        return (
          <div key={match.ruleId} className="flex flex-col gap-1.5 py-2">
            <div className="flex items-center gap-2">
              <RuleDot color={rule.color} />
              <span className="label-sm">{rule.name}</span>
            </div>
            {match.conditions.map(({ filter, value: actual, detail }, index) => {
              const shown = formatConditionValue(filter.field, actual)
              return (
                <div
                  key={`${filter.field}-${index}`}
                  className="flex flex-col items-start gap-1 pl-4"
                >
                  <FilterSummaryPill
                    filter={filter}
                    allFilterFields={allFilterFields}
                    getUserName={getUserName}
                  />
                  {shown && (
                    <span className="pl-1 text-fg-subtle">
                      <span className="label-sm text-fg-base">{shown}</span>
                      {detail && ` · ${detail}`}
                    </span>
                  )}
                  {linkTargetOf(filter.field) && (
                    <div className="pt-0.5">
                      <DemandLink
                        target={linkTargetOf(filter.field)!}
                        variantId={value.variantId}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}
    </TooltipMessageCard>
  )
}
